import { Box, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK } from "../../../../../theme/tokens";

type BaseProps = {
	title: string;
	/** Trails the title on its line, e.g. an `InfoIconWithTooltip` holding a longer explanation. */
	titleAdornment?: ReactNode;
	description: string;
};

type CountProps =
	| { count?: never; shown?: never; truncated?: never }
	| { count: number; shown: number; truncated: true }
	| { count: number; shown?: never; truncated: false };

type Props = BaseProps & CountProps;

type CountedSection = { total: number; rows: readonly unknown[]; truncated: boolean };

/** Build the count contract from an API section without permitting an invalid truncated state. */
export const sectionHeadingCountProps = (section: CountedSection): CountProps =>
	section.truncated ? { count: section.total, shown: section.rows.length, truncated: true } : { count: section.total, truncated: false };

export const SectionHeading: FC<Props> = (props) => {
	const { t } = useTranslation();
	const { title, titleAdornment, description } = props;
	return (
		<Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
			<Box sx={{ minWidth: 0 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					<Typography component="h3" sx={{ color: INK.SECONDARY, fontWeight: 700, fontSize: FONT_SIZE.EMPHASIS }}>
						{title}
					</Typography>
					{titleAdornment}
				</Box>
				<Typography component="p" sx={{ color: "text.secondary", fontSize: FONT_SIZE.SECONDARY, mt: 0.25 }}>
					{description}
				</Typography>
			</Box>
			{props.count !== undefined && (
				<Typography component="span" sx={{ color: "text.secondary", fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, whiteSpace: "nowrap" }}>
					{props.truncated
						? t("integration.summary.showing.count.of", { shown: props.shown, count: props.count })
						: t("integration.summary.items.count", { count: props.count })}
				</Typography>
			)}
		</Box>
	);
};
